import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import { FaTrain } from 'react-icons/fa';
import ThemeToggle from '../Common/ThemeToggle';
import styles from './Header.module.css';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const mainNavLinks = [
    { path: '/', label: 'Home' },
    { path: '/pnr-status', label: 'PNR Status' },
    { path: '/train-status', label: 'Train Status' },
  ];

  const moreNavLinks = [
    { path: '/train-info', label: 'Train Information' },
    { path: '/live-station', label: 'Live at Station' },
    { path: '/search-trains', label: 'Search Trains' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const isMoreActive = () => {
    return moreNavLinks.some(link => location.pathname.startsWith(link.path));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsMoreDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className={styles.header}>
      <div className={`container ${styles.headerContainer}`}>
        <div className={styles.headerContent}>
          {/* Logo */}
          <Link to="/" className={styles.logo}>
            <div className={styles.logoIcon}>
              <FaTrain />
            </div>
            <div className={styles.logoTextWrapper}>
              <span className={styles.logoText}>RailTracker</span>
              <span className={styles.logoTagline}>Live Train Updates</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className={styles.desktopNav}>
            {mainNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${styles.navLink} ${isActive(link.path) ? styles.active : ''}`}
              >
                {link.label}
              </Link>
            ))}

            {/* More Dropdown */}
            <div className={styles.moreDropdown} ref={dropdownRef}>
              <button
                className={`${styles.navLink} ${styles.moreButton} ${isMoreActive() ? styles.active : ''}`}
                onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)}
              >
                More
                <FiChevronDown className={`${styles.chevron} ${isMoreDropdownOpen ? styles.rotated : ''}`} />
              </button>

              {isMoreDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {moreNavLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`${styles.dropdownItem} ${isActive(link.path) ? styles.active : ''}`}
                      onClick={() => setIsMoreDropdownOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Right Section */}
          <div className={styles.rightSection}>
            <ThemeToggle />
            <button
              className={styles.menuButton}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className={styles.mobileNav}>
            {mainNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${styles.mobileNavLink} ${isActive(link.path) ? styles.active : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            
            <div className={styles.mobileNavDivider}>More Services</div>
            
            {moreNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${styles.mobileNavLink} ${isActive(link.path) ? styles.active : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;